/**
 * Live data for Now: a load repeated every few seconds that keeps what it has while the next answer is on
 * its way, and rests while the tab is hidden (catching up as soon as it is seen again). And a clock that
 * ticks, for the live time and the ages next to things.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api.ts';

export interface Live<T> {
  data?: T;
  error?: string;
  /** Nothing has arrived yet. */
  loading: boolean;
  /** A newer answer is on its way; `data` is the previous one. */
  refreshing: boolean;
  reload: () => void;
}

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

export function useLive<T>(load: () => Promise<T>, intervalMs: number, deps: unknown[] = []): Live<T> {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean; refreshing: boolean }>({ loading: true, refreshing: false });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const at = useRef(0);
  const key = JSON.stringify(deps);
  const lastKey = useRef(key);

  useEffect(() => {
    let cancelled = false;
    // Another tenant: what was shown belongs to the previous one
    if (lastKey.current !== key) {
      lastKey.current = key;
      setState({ loading: true, refreshing: true });
    } else {
      setState((s) => ({ ...s, refreshing: true }));
    }
    loadRef.current()
      .then((data) => {
        if (cancelled) return;
        at.current = Date.now();
        setState({ data, loading: false, refreshing: false });
      })
      .catch((e) => {
        if (cancelled) return;
        at.current = Date.now();
        setState((s) => ({ ...s, error: message(e), loading: false, refreshing: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [key, tick]);

  useEffect(() => {
    if (!intervalMs) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setTick((x) => x + 1);
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - at.current >= intervalMs) setTick((x) => x + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);

  return { ...state, reload: () => setTick((x) => x + 1) };
}

/** The time, re-read every `stepMs`: a component that reads it re-renders with it. */
export function useClock(stepMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), stepMs);
    return () => clearInterval(t);
  }, [stepMs]);
  return now;
}
